/**
 * Land Property Type Handler
 */

import { PropertyType } from '../../../shared/definitions/property';
import { PropertyTypeHandler, PropertyCategory, PropertyValidationRule } from '../base/property-type-handler.abstract';
import { RoomTypeConfig, FeatureConfig, FeatureCategory } from '@core/services/property-analysis/property-room-config.service';

export class LandHandler extends PropertyTypeHandler {
  readonly propertyType = PropertyType.Land;
  readonly category = PropertyCategory.Land;
  readonly displayName = 'Land';
  readonly pluralName = 'Land Plots';
  readonly icon = 'terrain';
  readonly description = 'Undeveloped or development-ready land';

  // Land doesn't have rooms in the traditional sense
  getRoomTypes(): RoomTypeConfig[] {
    return [];
  }

  getFeatures(): FeatureConfig[] {
    return [
      {
        key: 'hasWaterConnection',
        label: 'Water Connection',
        icon: 'water_drop',
        category: FeatureCategory.PROPERTY_ONLY,
        description: 'Municipal water connection available',
        detailsInterface: 'BuildingSystems',
        formFields: [
          { name: 'connectionType', label: 'Connection Type', type: 'select', options: [
            { value: 'municipal', label: 'Municipal' },
            { value: 'well', label: 'Private Well' },
            { value: 'none', label: 'Not Available' }
          ]},
          { name: 'hasWaterMeter', label: 'Has Water Meter', type: 'checkbox', default: false }
        ]
      },
      {
        key: 'hasElectricityConnection',
        label: 'Electricity Connection',
        icon: 'electrical_services',
        category: FeatureCategory.PROPERTY_ONLY,
        description: 'Electrical grid connection available',
        detailsInterface: 'BuildingSystems',
        formFields: [
          { name: 'connectionAvailable', label: 'Connection Available', type: 'checkbox', default: false },
          { name: 'distanceToGrid', label: 'Distance to Grid', type: 'number', suffix: 'm' }
        ]
      },
      {
        key: 'hasSewerConnection',
        label: 'Sewer Connection',
        icon: 'plumbing',
        category: FeatureCategory.PROPERTY_ONLY,
        description: 'Sewer system connection available',
        detailsInterface: 'BuildingSystems',
        formFields: [
          { name: 'connectionAvailable', label: 'Connection Available', type: 'checkbox', default: false }
        ]
      },
      {
        key: 'hasInternetConnection',
        label: 'Internet Connection',
        icon: 'wifi',
        category: FeatureCategory.PROPERTY_ONLY,
        description: 'Internet connection available',
        detailsInterface: 'ITInfrastructureDetail',
        formFields: [
          { name: 'connectionType', label: 'Connection Type', type: 'select', options: [
            { value: 'fiber', label: 'Fiber' },
            { value: 'cable', label: 'Cable' },
            { value: 'dsl', label: 'DSL' },
            { value: 'satellite', label: 'Satellite' },
            { value: 'none', label: 'Not Available' }
          ]}
        ]
      }
    ];
  }

  override getMainCharacteristics(): string[] {
    return ['landArea', 'frontageWidth', 'landType', 'zoning'];
  }

  override getValidationRules(): PropertyValidationRule[] {
    return [
      {
        fieldName: 'surfaces.land',
        rules: {
          required: true,
          min: 50,
          errorMessage: 'Land area must be at least 50 m²'
        }
      }
    ];
  }

  override getRecommendedMinSurface(): number {
    return 100;
  }

  override getPriceRangeMultiplier(): number {
    return 0.3; // Land is typically cheaper per m² than built properties
  }
}

